import React, { useState, useEffect } from 'react';
import AdminLayout from './AdminLayout';
import { api } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';

// NEW TOAST SYSTEM
import { useToast } from "../../contexts/ToastContext";

export default function VisaTypeManagement() {
  const { showToast } = useToast();
  const [visaCategories, setVisaCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVisaId, setEditingVisaId] = useState(null); // Store ID separately
  const [isEditing, setIsEditing] = useState(false); // Track edit mode

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    processing_time: '',
    validity: '',
    entries_allowed: '',
    fee: '',
    is_active: true,
    display_order: 0
  });

  useEffect(() => {
    fetchVisaTypes();
  }, []);

  const fetchVisaTypes = async () => {
    try {
      const response = await api.adminGetVisaTypes();
      setVisaCategories(response.data);
    } catch (error) {
      showToast("error", "Failed to load visa types");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (isEditing && editingVisaId) {
        console.log('Updating visa with ID:', editingVisaId);
        console.log('Form data:', formData);
        await api.adminUpdateVisaType(editingVisaId, formData);
        showToast("success", "Visa type updated");
      } else {
        console.log('Creating new visa');
        console.log('Form data:', formData);
        await api.adminCreateVisaType(formData);
        showToast("success", "Visa type created");
      }
      handleCloseDialog();
      fetchVisaTypes();
    } catch (error) {
      console.error('Submit error:', error);
      showToast("error", error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (visa) => {
    console.log('Editing visa:', visa);
    console.log('Visa ID:', visa.id);
    
    setIsEditing(true);
    setEditingVisaId(visa.id);
    setFormData({
      name: visa.name || '',
      description: visa.description || '',
      category: visa.category || '',
      processing_time: visa.processing_time || '',
      validity: visa.validity || '',
      entries_allowed: visa.entries_allowed || '',
      fee: visa.fee || '',
      is_active: visa.is_active !== undefined ? visa.is_active : true,
      display_order: visa.display_order || 0
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this visa type?')) return;
    
    try {
      await api.adminDeleteVisaType(id);
      showToast("success", "Visa type deleted");
      fetchVisaTypes();
    } catch (error) {
      showToast("error", error.response?.data?.detail || 'Delete failed');
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setIsEditing(false);
    setEditingVisaId(null);
    resetForm();
  };

  const handleOpenAddDialog = () => {
    setIsEditing(false);
    setEditingVisaId(null);
    resetForm();
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category: '',
      processing_time: '',
      validity: '',
      entries_allowed: '',
      fee: '',
      is_active: true,
      display_order: 0
    });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        
        {/* HEADER */}
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">Visa Type Management</h2>

          <Dialog open={dialogOpen} onOpenChange={(open) => {
            if (!open) {
              handleCloseDialog();
            }
          }}>
            <DialogTrigger asChild>
              <Button 
                onClick={handleOpenAddDialog}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Add Visa Type
              </Button>
            </DialogTrigger>

            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{isEditing ? 'Edit Visa Type' : 'Add Visa Type'}</DialogTitle>
              </DialogHeader>

              {/* FORM */}
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Name */}
                <div>
                  <Label>Name *</Label>
                  <Input
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                  />
                </div>

                {/* Description */}
                <div>
                  <Label>Description *</Label>
                  <Textarea
                    required
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                  />
                </div>

                {/* Category + Processing */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Category *</Label>
                    <Input
                      required
                      value={formData.category}
                      onChange={(e) => setFormData({...formData, category: e.target.value})}
                      placeholder="Tourist, Business..."
                    />
                  </div>

                  <div>
                    <Label>Processing Time *</Label>
                    <Input
                      required
                      value={formData.processing_time}
                      onChange={(e) => setFormData({...formData, processing_time: e.target.value})}
                      placeholder="3-5 business days"
                    />
                  </div>
                </div>

                {/* Validity + Entries */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Validity *</Label>
                    <Input
                      required
                      value={formData.validity}
                      onChange={(e) => setFormData({...formData, validity: e.target.value})}
                      placeholder="60 days"
                    />
                  </div>

                  <div>
                    <Label>Entries Allowed *</Label>
                    <Input
                      required
                      value={formData.entries_allowed}
                      onChange={(e) => setFormData({...formData, entries_allowed: e.target.value})}
                      placeholder="Single / Multiple"
                    />
                  </div>
                </div>

                {/* Fee + Order */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Fee (₹) *</Label>
                    <Input
                      type="number"
                      required
                      value={formData.fee}
                      onChange={(e) => setFormData({...formData, fee: e.target.value})}
                    />
                  </div>

                  <div>
                    <Label>Display Order</Label>
                    <Input
                      type="number"
                      value={formData.display_order}
                      onChange={(e) => setFormData({...formData, display_order: parseInt(e.target.value)})}
                    />
                  </div>
                </div>

                {/* Active Switch */}
                <div className="flex items-center space-x-2">
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({...formData, is_active: checked})}
                  />
                  <Label>Active</Label>
                </div>

                {/* SUBMIT BUTTON */}
                <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                  {isEditing ? 'Update' : 'Create'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* TABLE */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {visaCategories.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs text-gray-500 uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs text-gray-500 uppercase">Category</th>
                    {/* <th className="px-6 py-3 text-left text-xs text-gray-500 uppercase">Fee</th> */}
                    <th className="px-6 py-3 text-left text-xs text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200">
                  {visaCategories.flatMap((category) => 
                    category.visa_types.map((visa) => (
                      <tr key={visa.id}>
                        <td className="px-6 py-4 text-sm font-medium">{visa.name}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">{visa.category}</td>
                        {/* <td className="px-6 py-4 text-sm text-gray-600">₹{visa.fee}</td> */}
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            visa.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {visa.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>

                        <td className="px-6 py-4 space-x-2">
                          <Button
                            onClick={() => handleEdit(visa)}
                            variant="outline"
                            size="sm"
                          >
                            Edit
                          </Button>

                          <Button
                            onClick={() => handleDelete(visa.id)}
                            variant="outline"
                            size="sm"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                          >
                            Delete
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-12 text-center text-gray-500">
              No visa types found. Add one to get started.
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}